import React, { useMemo, useState } from 'react';
import {
  Title,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Text,
  Anchor,
  Center,
  Group,
  Stack,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../../hooks/useAuthenticatedData';
import config from '../../../../../config';
import MembershipPopup from '../../MembershipPopup';
import SQLEditorPopup from '../../SQLEditorPopup';

const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder>
    <Title order={6} size="sm" mb={4}>
      Legend
    </Title>
    <Stack gap={4}>
      {[
        { color: 'var(--mantine-color-green-6)', label: 'Margin ≥ 20%' },
        { color: 'var(--mantine-color-yellow-6)', label: 'Margin between 15% and 20%' },
        { color: 'var(--mantine-color-red-6)', label: 'Margin < 15%' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

function RepairsDepartmentRevenueDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';

  const url = `${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/inventory-revenue-details?period=${period}`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  const handleTabChange = (newPeriod) => {
    navigate({
      pathname: location.pathname,
      search: `?period=${newPeriod}`,
    });
  };

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index,
      // Extract values from the nested structure
      period: item.values?.period,
      fullname: item.values?.fullname,
      partnumber: item.values?.partnumber,
      salecondition: item.values?.salecondition,
      repaircondition: item.values?.repaircondition,
      linetypedesc: item.values?.linetypedesc,
      rcvrdocno: item.values?.rcvrdocno,
      rcvrline: item.values?.rcvrline,
      docno: item.values?.docno,
      line: item.values?.line,
      userline: item.values?.userline,
      logno: item.values?.logno,
      receiverdate: item.values?.receiverdate,
      invoicedate: item.values?.invoicedate,
      qtyshipped: item.values?.qtyshipped,
      histunitprice: item.values?.histunitprice,
      histpricedate: item.values?.histpricedate,
      currentunitprice: item.values?.currentunitprice,
      discountamt: item.values?.discountamt,
      projectedrevenue: item.values?.projectedrevenue,
      projectedmargindollars: item.values?.projectedmargindollars,
      projectedmarginpercent: item.values?.projectedmarginpercent,
      revenue: item.values?.revenue,
      cost: item.values?.cost,
      inventorycostinpart: item.values?.inventorycostinpart,
      margindollars: item.values?.margindollars,
      marginpercent: item.values?.marginpercent,
      dos: item.values?.dos,
      isber: item.values?.isber,
      onrepairteam: item.values?.onrepairteam,
    }));
  }, [data]);

  const generateExportFileName = () => `Repairs-${period}-revenue-details`;

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Period': row.period || '',
      'Repair Coordinator': row.fullname || '',
      'Part Number': row.partnumber || '',
      'Sale Condition': row.salecondition || '',
      'Repair Condition': row.repaircondition || '',
      'Line Type': row.linetypedesc || '',
      'Receiver Doc#': row.rcvrdocno || '',
      'Receiver Line': row.rcvrline || '',
      'Pentagon Doc#': row.docno || '',
      'System Line': row.line || '',
      'User Line': row.userline || '',
      'Log Number': row.logno || '',
      'Receiver Date': row.receiverdate ? new Date(row.receiverdate).toLocaleDateString() : '',
      'Invoice Date': row.invoicedate ? new Date(row.invoicedate).toLocaleDateString() : '',
      'Qty': row.qtyshipped || '',
      'Historical Unit Price': row.histunitprice || '',
      'Historical Price Date': row.histpricedate ? new Date(row.histpricedate).toLocaleDateString() : '',
      'Current Unit Price': row.currentunitprice || '',
      'Discount Amount': row.discountamt || '',
      'Projected Revenue': row.projectedrevenue || '',
      'Projected Margin ($)': row.projectedmargindollars || '',
      'Projected Margin %': row.projectedmarginpercent ? `${Number(row.projectedmarginpercent).toFixed(2)}%` : '',
      'Revenue': row.revenue || '',
      'Cost': row.cost || '',
      'Inventory Cost': row.inventorycostinpart || '',
      'Margin ($)': row.margindollars || '',
      'Margin %': row.marginpercent ? `${Number(row.marginpercent).toFixed(2)}%` : '',
      'DOS': row.dos || '',
      'BER?': row.isber === 'Y' ? 'Yes' : row.isber === 'N' ? 'No' : row.isber || '',
      'On Repair Team?': row.onrepairteam === 'Y' ? 'Yes' : row.onrepairteam === 'N' ? 'No' : row.onrepairteam || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  const columns = useMemo(
    () => [
      // Basic Context
      { accessorKey: 'period', header: 'Period', size: 80 },
      { accessorKey: 'fullname', header: 'Repair Coordinator', size: 150 },

      // Part Information
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      { accessorKey: 'salecondition', header: 'Sale Condition', size: 120 },
      { accessorKey: 'repaircondition', header: 'Repair Condition', size: 120 },
      { accessorKey: 'linetypedesc', header: 'Line Type', size: 120 },

      // Document Information
      { accessorKey: 'rcvrdocno', header: 'Receiver Doc#', size: 120 },
      { accessorKey: 'rcvrline', header: 'Receiver Line', size: 120 },
      { accessorKey: 'docno', header: 'Pentagon Doc#', size: 120 },
      { accessorKey: 'line', header: 'System Line', size: 120 },
      { accessorKey: 'userline', header: 'User Line', size: 80 },
      { accessorKey: 'logno', header: 'Log Number', size: 120 },

      // Date Information
      {
        accessorKey: 'receiverdate',
        header: 'Receiver Date',
        size: 120,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      {
        accessorKey: 'invoicedate',
        header: 'Invoice Date',
        size: 120,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },

      // Quantity
      {
        accessorKey: 'qtyshipped',
        header: 'Qty',
        size: 80,
        Cell: ({ cell }) => cell.getValue() != null ? Number(cell.getValue()).toLocaleString() : '',
      },

      // Pricing Expectations (Historical)
      {
        accessorKey: 'histunitprice',
        header: 'Historical Unit Price',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'histpricedate',
        header: 'Historical Price Date',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },

      // Pricing Expectations (Current)
      {
        accessorKey: 'currentunitprice',
        header: 'Current Unit Price',
        size: 140,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'discountamt',
        header: 'Discount Amount',
        size: 130,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },

      // Projected Financials
      {
        accessorKey: 'projectedrevenue',
        header: 'Projected Revenue',
        size: 140,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedmargindollars',
        header: 'Projected Margin ($)',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedmarginpercent',
        header: 'Projected Margin %',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },

      // Actual Financials
      {
        accessorKey: 'revenue',
        header: 'Revenue',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'cost',
        header: 'Cost',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : 'N/A',
      },
      {
        accessorKey: 'inventorycostinpart',
        header: 'Inventory Cost',
        size: 130,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'margindollars',
        header: 'Margin ($)',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'marginpercent',
        header: 'Margin %',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },

      // Status Information
      { accessorKey: 'dos', header: 'DOS', size: 80 },
      {
        accessorKey: 'isber',
        header: 'BER?',
        size: 70,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value === 'Y' ? 'Yes' : value === 'N' ? 'No' : value;
        },
      },
      {
        accessorKey: 'onrepairteam',
        header: 'On Repair Team?',
        size: 130,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value === 'Y' ? 'Yes' : value === 'N' ? 'No' : value;
        },
      },
    ],
    []
  );

  const getRowStyle = (row) => {
    const margin = Number(row.original.marginpercent);
    if (isNaN(margin)) return {};

    if (margin >= 20) {
      return {
        backgroundColor: 'var(--mantine-color-green-1)',
        color: 'var(--mantine-color-green-9)',
      };
    }
    if (margin >= 15 && margin < 20) {
      return {
        backgroundColor: 'var(--mantine-color-yellow-1)',
        color: 'var(--mantine-color-yellow-9)',
      };
    }
    if (margin < 15) {
      return {
        backgroundColor: 'var(--mantine-color-red-1)',
        color: 'var(--mantine-color-red-9)',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      <Text
        component="h1"
        style={{
          fontFamily: 'Orbitron, sans-serif',
          color: '#FDB813',
          fontStyle: 'italic',
          fontSize: '1.5rem',
          marginBottom: 0,
          textAlign: 'left'
        }}
      >
        Repairs Scorecard - Revenue Details
      </Text>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=repairs_dept_revenue_margin_summary',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=repairs_dept_revenue_margin_details',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="stretch" grow>
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Title order={6} size="sm" mb={4}>
            About the Data
          </Title>
          <Text size="xs" c="dimmed" mb="xs">
            Summary Data: These are sold parts that have been sent out for repair and received back. The gross margin is 
            based on the part cost, the repair cost, and the sold price. Warehouse base cost are not considered in this calculation.
          </Text>
          <Text size="xs" c="dimmed">
            Detail Data: These are all sold repaired parts. The projected values from when the repair was created, using historical part price data, 
            are compared to the actual values from when the repair was completed. The gross margin is based on the part cost, the repair cost, and the sold price.
          </Text>
        </Card>
        <ColorLegend />
      </Group>

      <Box mt="sm">
        <Tabs value={period} onChange={handleTabChange} mb="sm">
          <Tabs.List>
            <Tabs.Tab value="MTD">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="QTD">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="YTD">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      <Paper shadow="sm" p="sm" style={{ height: 600 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>

      {/* Pop-up dialogs */}
      <MembershipPopup opened={openMembership} onClose={() => setOpenMembership(false)} />
      <SQLEditorPopup
        opened={openSummaryQuery}
        onClose={() => setOpenSummaryQuery(false)}
        title="Summary Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/queries/inventory-revenue-summary-query`}
      />
      <SQLEditorPopup
        opened={openDetailsQuery}
        onClose={() => setOpenDetailsQuery(false)}
        title="Details Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/queries/inventory-revenue-details-query`}
      />
    </Paper>
  );
}

export default RepairsDepartmentRevenueDetails;